import { Question, PastPaperExam } from "../types";

export const IGCSE_QUIZ_QUESTIONS: Question[] = [
  // =========================================================================
  // UNIT 1: Motion, Forces & Energy / Measurements & Units
  // =========================================================================
  {
    id: "q-u1-2025-fm-1",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q1",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A student is asked to find the volume of a small irregularly shaped piece of rock. The student has a balance, a measuring cylinder containing water, and a ruler available.\nWhich apparatus must the student use to find the volume of the small piece of rock?",
    options: [
      "A. balance and ruler",
      "B. ruler only",
      "C. balance and measuring cylinder",
      "D. measuring cylinder only",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Measuring cylinder only (water displacement method).",
      "A balance measures mass, not volume.",
      "A ruler cannot measure the irregular dimensions.",
      "Option D is correct.",
    ],
    examinerExplanation: "For an irregularly shaped solid, volume is determined purely by the liquid displacement method using a measuring cylinder (submerging the rock and measuring the rise in water level).",
    formulaUsed: "Volume = Final Water Level - Initial Water Level",
  },
  {
    id: "q-u1-2025-fm-2",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q2",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A digital stop-clock measures time in minutes and seconds. The stop-clock reads 00:50 when it is started (i.e. 00 minutes 50 seconds). It reads 02:10 when it is stopped. What is the shortest possible time that has passed between starting and stopping the stop-clock?",
    options: [
      "A. 1 minute 06 seconds",
      "B. 1 minute 20 seconds",
      "C. 2 minutes 10 seconds",
      "D. 3 minutes 00 seconds",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Start: 50 s",
      "End: 2 min 10 s = 130 s",
      "Difference = 130 - 50 = 80 s = 1 min 20 s",
      "Option B is correct.",
    ],
    examinerExplanation: "Convert to seconds: 00:50 = 50 s. 02:10 = (2 × 60) + 10 = 130 s. Time elapsed = 130 - 50 = 80 s = 1 minute 20 seconds.",
    formulaUsed: "Elapsed Time = End Time - Start Time",
  },
  {
    id: "q-u1-2025-fm-3",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q3",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Four students are instructed to find the time it takes for a ball bearing to fall through a vertical height of 1.0 m. Which student follows the correct procedure to obtain an accurate result?",
    options: [
      "A. The student measures the time taken for the ball to fall 1.0 m five times. He then adds the times taken together and divides by five.",
      "B. The student measures the time taken for the ball to fall 1.0 m five times. He then multiplies the times taken together and divides by five.",
      "C. The student measures the time taken for the ball to fall 2.0 m. He then divides the time taken by two.",
      "D. The student measures the time taken for the ball to fall 2.0 m five times. He then adds the times taken together and divides by ten.",
    ],
    correctAnswer: "A",
    marks: 1,
    markScheme: [
      "Repeat measurements over the exact distance and calculate the mean (arithmetic average).",
      "Total Time / Number of trials = Mean time.",
      "Option A is correct.",
    ],
    examinerExplanation: "To reduce the effect of random timing errors and reaction time, repeat trials over the same distance (1.0 m) and calculate the mean average: (t1 + t2 + t3 + t4 + t5) / 5.",
  },
  {
    id: "q-u1-2025-fm-4",
    topicId: "unit-1",
    subTopicId: "1.2",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q4",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A speed-time graph shows the motion of a car for a 5-second period. Between 0 s and 3 s the speed increases linearly from 0 to 10 m/s. Between 3 s and 5 s the graph is a horizontal line at 10 m/s.\nWhich row shows a time when the car is at rest and a time when it is moving at a constant speed?",
    options: [
      "A. At rest at 0.0 s, constant speed at 2.0 s",
      "B. At rest at 0.0 s, constant speed at 4.0 s",
      "C. At rest at 4.0 s, constant speed at 0.0 s",
      "D. At rest at 4.0 s, constant speed at 2.0 s",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Speed = 0 m/s at t = 0.0 s (at rest).",
      "Speed is horizontal (constant) between 3.0 s and 5.0 s (e.g. at 4.0 s).",
      "Option B is correct.",
    ],
    examinerExplanation: "On a speed-time graph, 'at rest' means speed = 0 (at t = 0 s). 'Constant speed' means a horizontal line (zero gradient) parallel to the time axis, which occurs between 3 s and 5 s (such as at t = 4.0 s).",
  },
  {
    id: "q-u1-2025-fm-5",
    topicId: "unit-1",
    subTopicId: "1.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q5",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Which property of an object is a gravitational force acting on its mass?",
    options: [
      "A. density",
      "B. surface area",
      "C. volume",
      "D. weight",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Definition: Weight = mass × gravitational field strength (W = mg).",
      "Weight is the gravitational force on an object.",
      "Option D is correct.",
    ],
    examinerExplanation: "By definition in the Cambridge IGCSE syllabus, weight is the gravitational force exerted on an object that has mass.",
    formulaUsed: "W = m · g",
  },
  {
    id: "q-u1-2025-fm-6",
    topicId: "unit-1",
    subTopicId: "1.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q6",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A stone has a weight of 5.7 N. The gravitational field strength g is 9.8 N/kg. What is the mass of the stone?",
    options: [
      "A. 0.58 kg",
      "B. 5.7 kg",
      "C. 56 kg",
      "D. 550 kg",
    ],
    correctAnswer: "A",
    marks: 1,
    markScheme: [
      "Formula: W = mg => m = W / g",
      "Calculation: m = 5.7 / 9.8 = 0.5816 kg ≈ 0.58 kg",
      "Option A is correct.",
    ],
    examinerExplanation: "Rearrange W = mg to solve for mass: m = 5.7 N / 9.8 N/kg = 0.58 kg.",
    formulaUsed: "m = W / g",
  },
  {
    id: "q-u1-2025-fm-8",
    topicId: "unit-1",
    subTopicId: "1.5",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q8",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "What is meant by the moment of a force?",
    options: [
      "A. the magnitude of the force",
      "B. the work done by the force",
      "C. the time for which the force acts",
      "D. the turning effect of the force",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Moment of a force is the turning effect of the force about a pivot.",
      "Option D is correct.",
    ],
    examinerExplanation: "The moment of a force is defined as the measure of its turning effect about a pivot (Moment = Force × perpendicular distance).",
    formulaUsed: "Moment = F · d",
  },
  {
    id: "q-u1-2025-fm-9",
    topicId: "unit-1",
    subTopicId: "1.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q9",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A mass of 20 kg is held stationary by a rope passing over a frictionless pulley. What is the tension force T in the rope? (Take g = 9.8 N/kg or 10 N/kg)",
    options: [
      "A. 10 kg",
      "B. 20 kg",
      "C. 100 N",
      "D. 200 N",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Tension T balances the downward weight W of the mass.",
      "W = mg = 20 × 10 = 200 N (or 20 × 9.8 = 196 N ≈ 200 N).",
      "Option D is correct.",
    ],
    examinerExplanation: "For a stationary suspended mass, the upward tension T equals the downward gravitational weight: T = mg = 20 kg × 10 N/kg = 200 N.",
    formulaUsed: "T = W = m · g",
  },
  {
    id: "q-u1-2025-fm-11",
    topicId: "unit-1",
    subTopicId: "1.7",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q11",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A boy produces an average useful power output of 60 W as he rides his bicycle for 2.0 minutes. How much useful energy does he transfer?",
    options: [
      "A. 0.50 J",
      "B. 30 J",
      "C. 120 J",
      "D. 7200 J",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Time t = 2.0 minutes = 2.0 × 60 = 120 s",
      "Formula: Power = Energy / time => Energy = Power × time",
      "Energy = 60 W × 120 s = 7200 J",
      "Option D is correct.",
    ],
    examinerExplanation: "Always convert minutes to SI seconds before calculating energy: t = 2 × 60 = 120 s. Energy = Power × time = 60 × 120 = 7200 J.",
    formulaUsed: "E = P · t",
  },
  {
    id: "q-u1-2025-fm-12",
    topicId: "unit-1",
    subTopicId: "1.8",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q12",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A solid rectangular block has dimensions 80 cm × 40 cm × 20 cm. On which surface should the block rest to produce the SMALLEST pressure on the bench?\nSurface P (80 cm × 40 cm), Surface Q (40 cm × 20 cm), or Surface R (80 cm × 20 cm)?",
    options: [
      "A. Surface P",
      "B. Surface Q",
      "C. Surface R",
      "D. P, Q and R produce the same pressure",
    ],
    correctAnswer: "A",
    marks: 1,
    markScheme: [
      "Pressure = Force / Area",
      "Smallest pressure requires the LARGEST surface area in contact.",
      "Area P = 80 × 40 = 3200 cm² (largest area).",
      "Option A is correct.",
    ],
    examinerExplanation: "Pressure is inversely proportional to contact area (P = F / A). For the smallest pressure, place the block on the face with the largest area, which is P (80 cm × 40 cm = 3200 cm²).",
    formulaUsed: "P = F / A",
  },
  {
    id: "q-u1-2025-fm-13",
    topicId: "unit-1",
    subTopicId: "1.8",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q13",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A skier is standing still on a flat area of snow. The total weight of the skier is 550 N. The total area of his skis in contact with the ground is 0.015 m². What is the pressure exerted on the ground by the skier?",
    options: [
      "A. 0.83 N/m²",
      "B. 8.3 N/m²",
      "C. 3700 N/m²",
      "D. 37 000 N/m²",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "Formula: Pressure = Force / Area",
      "Calculation: P = 550 / 0.015 = 36 667 N/m² ≈ 37 000 N/m²",
      "Option D is correct.",
    ],
    examinerExplanation: "Pressure = Weight / Total Area = 550 N / 0.015 m² = 36 667 Pa ≈ 37 000 N/m².",
    formulaUsed: "P = F / A",
  },
  {
    id: "q-u1-2025-p32-q1",
    topicId: "unit-1",
    subTopicId: "1.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q1",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A speed-time graph shows a ball falling through the air from rest. At t = 1.0 s, the speed is 3.6 m/s. Between t = 2.0 s and 5.0 s, the speed remains constant at 4.0 m/s (terminal velocity).\n(a) Determine the speed of the ball at time = 1.0 s.\n(b) Describe the speed of the ball between time = 2.0 s and 5.0 s.\n(c) Calculate the distance moved by the ball between t = 2.0 s and t = 5.0 s.\n(d) The ball experiences an upward force X and a downward force Y at t = 3.0 s. Give the name of each force.",
    correctAnswer: "(a) 3.6 m/s, (b) Constant speed / terminal velocity (zero acceleration), (c) 12 m, (d) X = air resistance (drag), Y = weight (gravitational force)",
    marks: 7,
    markScheme: [
      "(a) 3.6 m/s [1 mark]",
      "(b) Constant speed / terminal velocity / zero acceleration [1 mark]",
      "(c) Distance = speed × time = 4.0 × (5.0 - 2.0) = 4.0 × 3.0 = 12 m [3 marks: formula (1), substitution (1), answer with unit (1)]",
      "(d) Force X: air resistance / drag [1 mark]; Force Y: weight / gravity / gravitational force [1 mark]",
    ],
    examinerExplanation: "When falling in air, the downward weight causes initial acceleration, but increasing air resistance eventually balances weight, leading to constant terminal velocity (zero acceleration). Distance during constant speed = v × t = 4 × 3 = 12 m.",
    formulaUsed: "distance = speed × time, W = mg",
  },
  {
    id: "q-u1-2025-p32-q2",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q2",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) Describe how a student uses a measuring cylinder, a beaker with liquid, and an electronic balance to determine the density of a liquid.\n(b) A metal block has dimensions 3.0 cm × 2.0 cm × 2.0 cm. The density of the metal is 8.0 g/cm³. Calculate the mass of the metal block.",
    correctAnswer: "(a) Measure mass of empty cylinder, add liquid and record volume V, measure total mass and calculate liquid mass m = m_total - m_empty, calculate density = m/V. (b) 96 g",
    marks: 7,
    markScheme: [
      "(a) 1. Measure mass of measuring cylinder on balance [1 mark]\n2. Pour liquid into cylinder and read volume V from scale [1 mark]\n3. Measure mass of cylinder + liquid, subtract to get liquid mass m, and compute density = m / V [1 mark]",
      "(b) Volume = length × width × height = 3.0 × 2.0 × 2.0 = 12 cm³ [2 marks]\nMass = density × volume = 8.0 g/cm³ × 12 cm³ = 96 g [2 marks]",
    ],
    examinerExplanation: "To find liquid density: mass of liquid = (mass of cylinder + liquid) - (mass of empty cylinder). Volume is read directly from the scale. For the solid block, V = 3 × 2 × 2 = 12 cm³, so mass = 8.0 × 12 = 96 g.",
    formulaUsed: "ρ = m / V => m = ρ · V",
  },
  {
    id: "q-u1-2025-p32-q3",
    topicId: "unit-1",
    subTopicId: "1.5",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q3",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) Explain why a solid cone is more stable than a solid cuboid of the same height.\n(b) The weight of a solid cuboid is 48 N. The area in contact with the ground is 32 cm². Calculate the pressure exerted on the ground in N/cm².\n(c) A horizontal force of 15 N tilts the cone at a perpendicular height of 18 cm from the pivot. Calculate the moment of the 15 N force about the pivot.",
    correctAnswer: "(a) The cone has a lower centre of gravity (and wider base), (b) 1.5 N/cm², (c) 270 N·cm (or 2.7 N·m)",
    marks: 7,
    markScheme: [
      "(a) The cone has a lower centre of gravity / centre of mass closer to base [1 mark]",
      "(b) Pressure = Force / Area = 48 N / 32 cm² = 1.5 N/cm² [3 marks]",
      "(c) Moment = Force × perpendicular distance = 15 N × 18 cm = 270 N·cm (or 15 × 0.18 = 2.7 N·m) [3 marks]",
    ],
    examinerExplanation: "Stability increases when the centre of gravity is lower and the base is wider. Moment = F × d = 15 × 18 = 270 N·cm. Pressure = 48 / 32 = 1.5 N/cm².",
    formulaUsed: "P = F / A, Moment = F · d",
  },
  {
    id: "q-u1-cam-top-2017-q3",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/43/M/J/17 Q3",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "A block of wood has a volume of 210 cm³ and a mass of 180 g.\n(a) Calculate the density of the block of wood.\n(b) The block is held just above the surface of a liquid of density 0.88 g/cm³. Predict and explain what happens when the block is released.",
    correctAnswer: "(a) 0.86 g/cm³, (b) The block floats because the density of the wood (0.86 g/cm³) is less than the density of the liquid (0.88 g/cm³).",
    marks: 4,
    markScheme: [
      "(a) Formula: ρ = m / V = 180 / 210 [C1]\nDensity = 0.86 g/cm³ (or 860 kg/m³) [A1]",
      "(b) Prediction: floats [B1]\nExplanation: density of wood is less than the density of the liquid (0.86 < 0.88) [B1]",
    ],
    examinerExplanation: "Calculate density using ρ = m/V = 180/210 = 0.86 g/cm³. Since 0.86 g/cm³ < 0.88 g/cm³, the wood is less dense than the liquid, so it floats in equilibrium.",
    formulaUsed: "ρ = m / V",
  },
  {
    id: "q-u1-cam-top-2017-q1",
    topicId: "unit-1",
    subTopicId: "1.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/O/N/17 Q1",
    yearSession: "Oct/Nov 2017",
    difficulty: "Extended",
    questionText: "A cylinder is made from copper of density 9000 kg/m³. The volume of the cylinder is 75 cm³.\n(a) Calculate the mass of the cylinder in kg.\n(b) Taking gravitational field strength g = 10 N/kg, calculate the weight of the cylinder.\n(c) State one way in which weight differs from mass.",
    correctAnswer: "(a) 0.68 kg (or 675 g), (b) 6.8 N, (c) Weight has direction (vector) / weight is a gravitational force, whereas mass is a scalar / amount of matter.",
    marks: 5,
    markScheme: [
      "(a) Conversion: 75 cm³ = 75 × 10⁻⁶ m³ = 7.5 × 10⁻⁵ m³ [C1]\nMass m = ρ · V = 9000 × 7.5 × 10⁻⁵ = 0.675 kg ≈ 0.68 kg [A1]",
      "(b) Weight W = m · g = 0.675 × 10 = 6.75 N ≈ 6.8 N [A1]",
      "(c) Any valid distinction: weight is a vector/force, mass is a scalar/quantity of matter; weight varies with gravitational field, mass remains constant [B1]",
    ],
    examinerExplanation: "Convert cm³ to m³: 75 cm³ = 75 × 10⁻⁶ m³. Mass = 9000 × 7.5 × 10⁻⁵ = 0.675 kg. Weight = 0.675 × 10 = 6.75 N.",
    formulaUsed: "m = ρ · V, W = m · g",
  },
  {
    id: "q-u1-cam-top-2017-impulse",
    topicId: "unit-1",
    subTopicId: "1.6",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/17 Q2",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "A footballer kicks a ball vertically upwards. Initially, the ball is stationary. His boot is in contact with the ball for 0.050 s. The average resultant force on the ball during this time is 180 N. The ball leaves his foot at 20 m/s.\n(a) Calculate the impulse of the force acting on the ball.\n(b) Calculate the mass of the ball.\n(c) Calculate the maximum height to which the ball rises (ignore air resistance, g = 10 m/s²).",
    correctAnswer: "(a) 9.0 N·s, (b) 0.45 kg, (c) 20 m",
    marks: 7,
    markScheme: [
      "(a) Impulse = Force × time = 180 × 0.050 [C1] = 9.0 N·s (or 9.0 kg·m/s) [A1]",
      "(b) Impulse = m · Δv => 9.0 = m × 20 [C1] => m = 0.45 kg [A1]",
      "(c) Kinetic Energy = Gravitational PE => ½mv² = mgh => h = v² / 2g = 20² / (2 × 10) = 400 / 20 = 20 m [C1, A1]",
    ],
    examinerExplanation: "Impulse = F · Δt = 180 × 0.05 = 9.0 N·s. Since Impulse = m(v - u), m = 9.0 / 20 = 0.45 kg. By energy conservation, mgh = ½mv² => h = v²/(2g) = 400/20 = 20 m.",
    formulaUsed: "Impulse = F · Δt = m · Δv, mgh = ½mv²",
  },
  {
    id: "q-u1-cam-top-2017-collision",
    topicId: "unit-1",
    subTopicId: "1.6",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/43/M/J/17 Q2",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "A metal block A of mass 3.2 kg travelling at 4.0 m/s on a smooth surface collides with a second stationary block B of mass 1.6 kg. After the collision, the velocity of A is 1.5 m/s in the same direction.\n(a) Calculate the momentum of A before the collision.\n(b) Calculate the velocity v of B after the collision.\n(c) The collision lasts for 0.050 s. Calculate the average force exerted on B during the collision.",
    correctAnswer: "(a) 12.8 kg·m/s, (b) 5.0 m/s, (c) 160 N",
    marks: 7,
    markScheme: [
      "(a) Momentum p = m · v = 3.2 × 4.0 = 12.8 kg·m/s (or 13 kg·m/s) [C1, A1]",
      "(b) By Conservation of Momentum: Total Initial = Total Final\n12.8 = (3.2 × 1.5) + (1.6 × v) [C1]\n12.8 = 4.8 + 1.6v => 1.6v = 8.0 => v = 5.0 m/s [A1]",
      "(c) Force on B: F = Δp / Δt = (1.6 × 5.0) / 0.050 = 8.0 / 0.050 = 160 N [C1, A1]",
    ],
    examinerExplanation: "Conservation of momentum states total initial momentum = total final momentum. Initial = 3.2 × 4.0 = 12.8 kg·m/s. Final = (3.2 × 1.5) + (1.6 × v). 12.8 = 4.8 + 1.6v => v = 5.0 m/s. Force = Δp/t = 8.0 / 0.050 = 160 N.",
    formulaUsed: "p = mv, m1u1 + m2u2 = m1v1 + m2v2, F = Δp / Δt",
  },
  {
    id: "q-u1-cam-top-2018-hooke",
    topicId: "unit-1",
    subTopicId: "1.4",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/O/N/17 Q2",
    yearSession: "Oct/Nov 2017",
    difficulty: "Extended",
    questionText: "(a) State Hooke's Law.\n(b) For forces up to 120 N, a spring obeys Hooke's Law and extends by 64 mm. Calculate the spring constant k of the spring in N/m (or N/mm).\n(c) When tested with a weight of 140 N, the reading on the spring balance is not 140 N. Suggest and explain why.",
    correctAnswer: "(a) Extension is directly proportional to load provided the limit of proportionality is not exceeded. (b) 1875 N/m (or 1.88 N/mm). (c) The load exceeds the limit of proportionality / elastic limit.",
    marks: 5,
    markScheme: [
      "(a) Extension is directly proportional to load / force (F = kx) [B1]",
      "(b) k = F / x = 120 / 0.064 m = 1875 N/m (or 120 / 64 = 1.88 N/mm) [C1, A1]",
      "(c) The spring has been stretched past its limit of proportionality / elastic limit, so it no longer obeys Hooke's law [B1]",
    ],
    examinerExplanation: "Hooke's Law: F = kx. k = 120 N / 0.064 m = 1875 N/m. At 140 N, the spring exceeds its proportional limit and deforms plastically.",
    formulaUsed: "F = k · x",
  },
  {
    id: "q-u1-cam-top-2022-pulley",
    topicId: "unit-1",
    subTopicId: "1.3",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/M/J/22 Q2",
    yearSession: "May/June 2022",
    difficulty: "Challenging",
    questionText: "An object of mass 2.0 kg on a bench is connected by a cord over a pulley to a suspended mass of 3.0 kg. The 2.0 kg object is released from rest and accelerates at 4.0 m/s².\n(a) Calculate the resultant force acting on the 2.0 kg object.\n(b) Calculate the upward force F (tension) exerted by the cord on the 3.0 kg object (g = 10 m/s²).\n(c) Calculate the speed of the objects 0.80 s after release.",
    correctAnswer: "(a) 8.0 N, (b) 18 N, (c) 3.2 m/s",
    marks: 7,
    markScheme: [
      "(a) F_net = m · a = 2.0 × 4.0 = 8.0 N [C1, A1]",
      "(b) Downward weight on 3.0 kg mass = mg = 3.0 × 10 = 30 N [C1]\nResultant force downward = ma = 3.0 × 4.0 = 12 N [C1]\nWeight - Tension = Resultant => 30 - F = 12 => F = 18 N [A1]",
      "(c) v = u + at = 0 + (4.0 × 0.80) = 3.2 m/s [C1, A1]",
    ],
    examinerExplanation: "Newton's 2nd Law for 2.0 kg object: F_net = 2.0 × 4.0 = 8.0 N. For the 3.0 kg falling object: W - T = ma => (3.0 × 10) - T = 3.0 × 4.0 => 30 - T = 12 => T = 18 N. Speed v = at = 4.0 × 0.80 = 3.2 m/s.",
    formulaUsed: "F = ma, v = u + at",
  },

  // =========================================================================
  // UNIT 2: Thermal Physics
  // =========================================================================
  {
    id: "q-u2-2025-fm-15",
    topicId: "unit-2",
    subTopicId: "2.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q15",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A cylinder containing oxygen gas under high pressure is cooled so its temperature decreases. Which statement describes what happens to the pressure in the cylinder and gives the reason why?",
    options: [
      "A. The pressure decreases because the oxygen particles move faster.",
      "B. The pressure decreases because the oxygen particles move more slowly.",
      "C. The pressure increases because the oxygen particles move faster.",
      "D. The pressure increases because the oxygen particles move more slowly.",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Lower temperature means lower average kinetic energy of gas molecules.",
      "Particles move more slowly, causing fewer and less forceful collisions with cylinder walls.",
      "Pressure decreases.",
      "Option B is correct.",
    ],
    examinerExplanation: "Temperature is directly proportional to average kinetic energy. When cooled, particles slow down, colliding less frequently and with smaller change of momentum against walls, so pressure decreases.",
    formulaUsed: "P ∝ T (at constant volume)",
  },
  {
    id: "q-u2-2025-fm-17",
    topicId: "unit-2",
    subTopicId: "2.2",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q17",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Solids expand the ......1...... and the average separation between molecules is the ......2...... .\nWhich words complete the sentence about the expansion of solids when they are heated, relative to liquids and gases?",
    options: [
      "A. 1: least | 2: greatest",
      "B. 1: least | 2: smallest",
      "C. 1: most | 2: greatest",
      "D. 1: most | 2: smallest",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Solids expand the least upon heating because of strong intermolecular bonds.",
      "The average separation between molecules in a solid is the smallest.",
      "Option B is correct.",
    ],
    examinerExplanation: "Thermal expansion is lowest in solids and highest in gases. The intermolecular spacing in solids is also the smallest among the three states of matter.",
  },
  {
    id: "q-u2-2025-fm-20",
    topicId: "unit-2",
    subTopicId: "2.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q20",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A candle is used to heat some water in a glass beaker. What is the main method of heat transfer through the glass beaker and within the water?",
    options: [
      "A. through the glass: conduction | within the water: conduction",
      "B. through the glass: conduction | within the water: convection",
      "C. through the glass: convection | within the water: conduction",
      "D. through the glass: convection | within the water: convection",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Through solid glass: Conduction (vibrating atoms in lattice).",
      "Within bulk liquid water: Convection (buoyancy of heated, less dense fluid).",
      "Option B is correct.",
    ],
    examinerExplanation: "Conduction occurs in solid materials (the glass). In fluids (water), convection occurs as warmer water expands, decreases in density, and rises, setting up a convection current.",
  },
  {
    id: "q-u2-2025-p32-q6",
    topicId: "unit-2",
    subTopicId: "2.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q6",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "A student warms water in a metal container using an electric hotplate.\n(a) Describe the arrangement, separation, and motion of particles in liquid water.\n(b) State how thermal energy moves through the bottom of the metal container to the water.\n(c) Describe how thermal energy spreads throughout the water.\n(d) State the temperature at which water boils under standard atmospheric pressure.",
    correctAnswer: "(a) Random arrangement, close together (touching), slide past each other randomly. (b) Conduction. (c) Convection: bottom water warms, expands, density decreases and it rises, colder water sinks creating convection currents. (d) 100 °C.",
    marks: 8,
    markScheme: [
      "(a) Arrangement: irregular / random [1 mark]; Separation: close together / touching [1 mark]; Motion: slide / move freely past each other [1 mark]",
      "(b) Conduction (delocalised free electrons and lattice vibration) [1 mark]",
      "(c) Convection current: water at base heats up and expands [1 mark]; density decreases and warmer water rises [1 mark]; denser cool water sinks to replace it [1 mark]",
      "(d) 100 °C [1 mark]",
    ],
    examinerExplanation: "Liquid particles are close together, randomly arranged, and slide over each other. Thermal energy passes through the solid metal base by conduction and circulates through the liquid by convection.",
  },
  {
    id: "q-u2-cam-top-2017-q4",
    topicId: "unit-2",
    subTopicId: "2.3",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/17 Q4",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "(a) A 240 V, 60 W lamp is connected to a 240 V supply and operates at constant temperature. State the rate at which the lamp transfers energy to the surroundings.\n(b) A copper block is heated to 400 °C. One side is dull black; the other side is polished shiny silver. Two thermometers with black bulbs are placed at equal distances from each side.\nExplain any difference between the maximum temperature shown by the two thermometers.\n(c) State the benefit to a firefighter of wearing shiny silver-coloured clothing.",
    correctAnswer: "(a) 60 W (or 60 J/s). (b) Thermometer next to the dull black side shows a higher temperature because dull black surfaces are better emitters of thermal radiation than shiny silver. (c) Shiny silver reflects thermal radiation, reducing heat absorption.",
    marks: 6,
    markScheme: [
      "(a) 60 W or 60 J/s [B1]",
      "(b) Dull black surface is a better / more effective emitter of infrared radiation [B1]; Shiny surface is a poor emitter / reflector [B1]; Thermometer near black surface receives more thermal radiation per second [B1]",
      "(c) Shiny silver reflects infrared radiation / prevents firefighter from absorbing too much heat [B1]",
    ],
    examinerExplanation: "At constant temperature, power input = rate of energy loss = 60 W. Dull black surfaces are the best emitters and absorbers of infrared radiation, while polished silver surfaces are poor emitters and good reflectors.",
  },
  {
    id: "q-u2-cam-top-2018-shc",
    topicId: "unit-2",
    subTopicId: "2.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/O/N/18 Q4",
    yearSession: "Oct/Nov 2018",
    difficulty: "Extended",
    questionText: "An electric heater of voltage 12 V and current 3.8 A is used to heat an iron block of mass 2.0 kg for 10 minutes (600 s). The temperature of the block rises from 25 °C to 55 °C.\nCalculate the specific heat capacity of the iron block.",
    correctAnswer: "450 J/(kg·°C)",
    marks: 4,
    markScheme: [
      "Energy supplied E = V · I · t = 12 × 3.8 × 600 = 27 360 J (or 27 000 J) [C1]",
      "Temperature rise ΔT = 55 - 25 = 30 °C [C1]",
      "Formula: c = E / (m · ΔT) = 27 360 / (2.0 × 30) [C1]",
      "Specific Heat Capacity c = 456 J/(kg·°C) (allow 450 to 460 J/(kg·°C)) [A1]",
    ],
    examinerExplanation: "Electrical energy supplied E = VIt = 12 × 3.8 × 600 = 27 360 J. Specific heat capacity c = E / (m · ΔT) = 27 360 / (2.0 × 30) = 456 J/(kg·°C).",
    formulaUsed: "E = VIt, E = mcΔT => c = E / (mΔT)",
  },
  {
    id: "q-u2-cam-top-2018-latent",
    topicId: "unit-2",
    subTopicId: "2.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/18 Q8",
    yearSession: "May/June 2018",
    difficulty: "Extended",
    questionText: "In a laboratory, 200 g of water at 22 °C is cooled by adding small pieces of ice at 0 °C one by one until the temperature of the mixture reaches 0 °C. The total mass of ice melted is 60 g. Specific heat capacity of water is 4.2 J/(g·°C).\n(a) Calculate the thermal energy lost by the water originally in the beaker.\n(b) Assuming all thermal energy lost by water is transferred to the ice, calculate the specific latent heat of fusion of ice in J/g.",
    correctAnswer: "(a) 18 480 J (or 18.5 kJ), (b) 308 J/g (or 3.08 × 10⁵ J/kg)",
    marks: 4,
    markScheme: [
      "(a) Q = m · c · ΔT = 200 × 4.2 × 22 = 18 480 J (or 18 000 J) [C1, A1]",
      "(b) Q = m_ice · L_f => 18 480 = 60 × L_f => L_f = 18 480 / 60 = 308 J/g (or 3.1 × 10⁵ J/kg) [C1, A1]",
    ],
    examinerExplanation: "Heat lost by water cooling to 0°C = mcΔT = 200 g × 4.2 J/g°C × 22°C = 18 480 J. By energy conservation, this heat melts 60 g of ice: Lf = Q / m_ice = 18 480 / 60 = 308 J/g.",
    formulaUsed: "Q = mcΔT, Q = mLf",
  },

  // =========================================================================
  // UNIT 3: Waves, Light & Sound
  // =========================================================================
  {
    id: "q-u3-2025-fm-21",
    topicId: "unit-3",
    subTopicId: "3.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q21",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Which equation enables the speed of a wave to be calculated?",
    options: [
      "A. speed = amplitude × frequency",
      "B. speed = amplitude × wavelength",
      "C. speed = frequency × wavelength",
      "D. speed = frequency / wavelength",
    ],
    correctAnswer: "C",
    marks: 1,
    markScheme: [
      "Wave equation: v = f · λ (speed = frequency × wavelength).",
      "Option C is correct.",
    ],
    examinerExplanation: "The fundamental wave equation states that wave speed (v) equals frequency (f) multiplied by wavelength (λ).",
    formulaUsed: "v = f · λ",
  },
  {
    id: "q-u3-2025-fm-24",
    topicId: "unit-3",
    subTopicId: "3.2",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q24",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "An object is placed in front of a converging lens at a distance greater than twice the focal length (2F). Rays pass through the lens and form an image on a screen.\nWhich description of the image formed by the lens is correct?",
    options: [
      "A. real, inverted and diminished",
      "B. real, upright and enlarged",
      "C. virtual, inverted and diminished",
      "D. virtual, upright and enlarged",
    ],
    correctAnswer: "A",
    marks: 1,
    markScheme: [
      "Image formed on screen => Real.",
      "Rays cross axis => Inverted.",
      "Object beyond 2F => Diminished.",
      "Option A is correct.",
    ],
    examinerExplanation: "When an object is placed beyond 2F of a converging lens, the refracted rays converge on the opposite side between F and 2F to produce a real, inverted, and diminished image.",
  },
  {
    id: "q-u3-2025-fm-27",
    topicId: "unit-3",
    subTopicId: "3.4",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q27",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "An athlete hears a starting pistol fire 1.5 seconds after she sees a puff of smoke from the pistol. The sound and smoke were produced simultaneously at a distance of 450 metres away.\nWhat is the speed of sound calculated from this observation?",
    options: [
      "A. 150 m/s",
      "B. 300 m/s",
      "C. 330 m/s",
      "D. 675 m/s",
    ],
    correctAnswer: "B",
    marks: 1,
    markScheme: [
      "Formula: Speed = Distance / Time",
      "Calculation: v = 450 / 1.5 = 300 m/s",
      "Option B is correct.",
    ],
    examinerExplanation: "Since light travels almost instantaneously (3 × 10⁸ m/s), the 1.5 s delay is the time taken for the sound wave to travel 450 m: v = d / t = 450 / 1.5 = 300 m/s.",
    formulaUsed: "v = d / t",
  },
  {
    id: "q-u3-2025-p32-q7",
    topicId: "unit-3",
    subTopicId: "3.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q7",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) White light is dispersed into a spectrum by a glass prism. The first and last colours are Red and Violet. State the other five colours in the correct order.\n(b) In the electromagnetic spectrum chart: Radio | Microwaves | Infrared | Visible Light | [Unlabelled] | X-rays | Gamma rays.\n(i) State the name of the unlabelled region.\n(ii) Describe one medical use of gamma rays.\n(iii) Describe one harmful effect of excessive exposure to gamma rays.",
    correctAnswer: "(a) Orange, Yellow, Green, Blue, Indigo. (b)(i) Ultraviolet (UV), (ii) Treating cancer (radiotherapy) / sterilising medical equipment, (iii) Cell mutation / cancer / burns.",
    marks: 6,
    markScheme: [
      "(a) Orange, Yellow, Green, Blue, Indigo (in exact sequence) [1 mark]",
      "(b)(i) Ultraviolet / UV [1 mark]",
      "(b)(ii) Radiotherapy / treating cancer tumours / sterilising surgical tools [1 mark]",
      "(b)(iii) Damage to DNA / cell mutation / cancer / tissue destruction [1 mark]",
    ],
    examinerExplanation: "Remember the mnemonic ROYGBIV: Red, Orange, Yellow, Green, Blue, Indigo, Violet. Ultraviolet sits between visible light and X-rays. Gamma rays are high-energy ionising radiation used in targeted cancer therapy.",
  },
  {
    id: "q-u3-cam-top-2017-snell",
    topicId: "unit-3",
    subTopicId: "3.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/M/J/17 Q7",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "The speed of light in air is 3.0 × 10⁸ m/s. The speed of light in a transparent liquid is 2.0 × 10⁸ m/s. A ray of light in air is incident on the surface of the liquid at an angle of incidence of 40°.\n(a) Calculate the refractive index n of the liquid.\n(b) Calculate the angle of refraction r in the liquid.",
    correctAnswer: "(a) 1.50, (b) 25.4° (or 25°)",
    marks: 4,
    markScheme: [
      "(a) Refractive index n = c_air / v_liquid = (3.0 × 10⁸) / (2.0 × 10⁸) = 1.50 [C1, A1]",
      "(b) Snell's Law: n = sin(i) / sin(r) => 1.50 = sin(40°) / sin(r) [C1]\nsin(r) = sin(40°) / 1.50 = 0.6428 / 1.50 = 0.4285 => r = sin⁻¹(0.4285) = 25.4° [A1]",
    ],
    examinerExplanation: "Refractive index n = c / v = 3.0×10⁸ / 2.0×10⁸ = 1.50. From n = sin i / sin r: sin r = sin 40° / 1.50 = 0.4285, so r = 25.4°.",
    formulaUsed: "n = c / v, n = sin(i) / sin(r)",
  },
  {
    id: "q-u3-cam-top-2021-optic-fibre",
    topicId: "unit-3",
    subTopicId: "3.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/43/O/N/21 Q7",
    yearSession: "Oct/Nov 2021",
    difficulty: "Extended",
    questionText: "An optical fibre made of glass has a refractive index of 1.40.\n(a) State the process that allows light to travel along the core of the optical fibre without escaping.\n(b) Calculate the critical angle c for the glass-air boundary of the fibre.\n(c) State two advantages of optical fibres over copper cables in telecommunications.",
    correctAnswer: "(a) Total internal reflection (TIR). (b) 45.6° (or 46°). (c) Higher data transmission rate / greater bandwidth AND less signal attenuation / lighter weight.",
    marks: 5,
    markScheme: [
      "(a) Total internal reflection (TIR) [B1]",
      "(b) sin(c) = 1 / n = 1 / 1.40 = 0.7143 => c = sin⁻¹(0.7143) = 45.6° (or 46°) [C1, A1]",
      "(c) Any two: faster data transfer / less energy loss over distance / no electromagnetic interference / lighter cables [B2]",
    ],
    examinerExplanation: "Light hits the inside core boundary at an angle of incidence greater than the critical angle (i > c), undergoing 100% total internal reflection. Critical angle c = sin⁻¹(1/n) = sin⁻¹(1/1.4) = 45.6°.",
    formulaUsed: "sin(c) = 1 / n",
  },

  // =========================================================================
  // UNIT 4: Electricity & Magnetism
  // =========================================================================
  {
    id: "q-u4-2025-fm-29",
    topicId: "unit-4",
    subTopicId: "4.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q29",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Why are electrical connecting cables often made from copper?",
    options: [
      "A. Copper contains free electrons and is a good electrical conductor.",
      "B. Copper contains free electrons and is a good electrical insulator.",
      "C. Copper contains free protons and is a good electrical conductor.",
      "D. Copper contains free protons and is a good electrical insulator.",
    ],
    correctAnswer: "A",
    marks: 1,
    markScheme: [
      "Metals have delocalised / free electrons that move easily when a voltage is applied.",
      "Option A is correct.",
    ],
    examinerExplanation: "Metals like copper conduct electricity through the movement of free (delocalised) electrons. Protons are tightly bound inside the atomic nuclei and cannot flow.",
  },
  {
    id: "q-u4-2025-fm-32",
    topicId: "unit-4",
    subTopicId: "4.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q32",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Four 2.0 V cells are connected together in series to form a battery. What is the combined electromotive force (e.m.f.) of the battery?",
    options: [
      "A. 0.50 V",
      "B. 2.0 V",
      "C. 6.0 V",
      "D. 8.0 V",
    ],
    correctAnswer: "D",
    marks: 1,
    markScheme: [
      "For cells connected in series assisting each other: E_total = E1 + E2 + E3 + E4",
      "E_total = 2.0 + 2.0 + 2.0 + 2.0 = 8.0 V",
      "Option D is correct.",
    ],
    examinerExplanation: "When cells are connected in series in the same orientation, their individual electromotive forces add together: 4 × 2.0 V = 8.0 V.",
    formulaUsed: "E_total = E1 + E2 + ... + En",
  },
  {
    id: "q-u4-2025-fm-34",
    topicId: "unit-4",
    subTopicId: "4.3",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q34",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "The potential difference across a car headlamp is 12 V. The current in the lamp is 2.5 A. How much energy is transferred by the lamp in 1.0 hour?",
    options: [
      "A. 1800 J",
      "B. 1800 W",
      "C. 108 000 J",
      "D. 108 000 W",
    ],
    correctAnswer: "C",
    marks: 1,
    markScheme: [
      "Time t = 1.0 h = 3600 s",
      "Formula: Energy E = V · I · t",
      "Calculation: E = 12 V × 2.5 A × 3600 s = 108 000 J (108 kJ)",
      "Option C is correct.",
    ],
    examinerExplanation: "Energy = V · I · t. 1.0 hour = 3600 seconds. Energy = 12 × 2.5 × 3600 = 108 000 J. Note that unit must be Joules (J), not Watts (W).",
    formulaUsed: "E = V · I · t",
  },
  {
    id: "q-u4-2025-p32-q9",
    topicId: "unit-4",
    subTopicId: "4.3",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q9",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) A circuit contains a battery, a switch, an ammeter, a lamp, a voltmeter in parallel across the lamp, and a variable resistor (rheostat) in series. Draw or complete the standard schematic symbols for this circuit.\n(b) The p.d. across the lamp is 11 V and the current is 0.44 A. Calculate the resistance of the lamp.\n(c) When the sliding contact on the rheostat is moved from position A to position B, the resistance of the circuit increases. State and explain the effect on the current.",
    correctAnswer: "(a) Standard circuit symbols for battery, switch, ammeter (series), voltmeter (parallel), rheostat (series). (b) 25 Ω. (c) Current decreases because total circuit resistance increases (I = V/R).",
    marks: 9,
    markScheme: [
      "(a) Correct standard circuit symbols for voltmeter (circle with V), variable resistor (rectangle with diagonal arrow), lamp (circle with cross), ammeter (circle with A) [4 marks]",
      "(b) Resistance R = V / I = 11 / 0.44 = 25 Ω [3 marks]",
      "(c) Current decreases [1 mark]; because total resistance of circuit increases and I = V / R_total [1 mark]",
    ],
    examinerExplanation: "Ohm's Law: R = V / I = 11 V / 0.44 A = 25 Ω. Increasing rheostat resistance increases the denominator in I = V / R, reducing current.",
    formulaUsed: "R = V / I",
  },
  {
    id: "q-u4-2025-p32-q10",
    topicId: "unit-4",
    subTopicId: "4.5",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q10",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) A permanent bar magnet is pushed into a solenoid connected to a sensitive voltmeter. Describe what is observed on the voltmeter.\n(b) State two changes to increase the induced electromotive force (e.m.f.).\n(c) A transformer has a 100 V a.c. primary supply with 40 turns on the primary coil and 80 turns on the secondary coil.\n(i) State a suitable material for the core.\n(ii) State the term used to describe this type of transformer.\n(iii) Calculate the secondary output voltage.",
    correctAnswer: "(a) The voltmeter needle deflects briefly and returns to zero. (b) 1. Move magnet faster, 2. Use stronger magnet (or more turns on solenoid). (c)(i) Soft iron, (ii) Step-up transformer, (iii) 200 V.",
    marks: 7,
    markScheme: [
      "(a) Needle deflects to one side while magnet is moving and returns to zero when motion stops [2 marks]",
      "(b) 1. Move magnet faster / higher speed [1 mark]\n2. Use stronger magnet / increase number of turns on coil [1 mark]",
      "(c)(i) Soft iron [1 mark]",
      "(c)(ii) Step-up transformer (since Ns > Np) [1 mark]",
      "(c)(iii) Vs = Vp × (Ns / Np) = 100 × (80 / 40) = 200 V [1 mark]",
    ],
    examinerExplanation: "Electromagnetic induction requires relative motion between magnetic field lines and the coil. Faraday's Law shows induced e.m.f. is proportional to rate of cutting flux. Transformer equation: Vs/Vp = Ns/Np => Vs = 100 × (80/40) = 200 V.",
    formulaUsed: "Vs / Vp = Ns / Np",
  },
  {
    id: "q-u4-cam-top-2018-potdiv",
    topicId: "unit-4",
    subTopicId: "4.3",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/17 Q9",
    yearSession: "May/June 2017",
    difficulty: "Extended",
    questionText: "A 6.0 V battery is connected in series with a fixed 1.2 kΩ resistor and a thermistor. A voltmeter is connected across the thermistor.\n(a) At a certain temperature, the resistance of the thermistor is 2.4 kΩ. Calculate the reading on the voltmeter.\n(b) State and explain what happens to the voltmeter reading when the temperature of the thermistor increases.",
    correctAnswer: "(a) 4.0 V, (b) Voltmeter reading decreases because thermistor resistance decreases with rising temperature, taking a smaller fraction of the total voltage.",
    marks: 5,
    markScheme: [
      "(a) Total resistance R_total = 1.2 kΩ + 2.4 kΩ = 3.6 kΩ [C1]\nCurrent I = V / R = 6.0 / 3600 = 1.67 × 10⁻³ A [C1]\nVoltmeter reading V_thermistor = I × R_thermistor = (1.67 × 10⁻³) × 2400 = 4.0 V [A1]\nOR by potential divider ratio: V = 6.0 × (2.4 / 3.6) = 4.0 V",
      "(b) Thermistor resistance decreases as temperature increases [B1]; therefore it takes a smaller share of the battery p.d., so voltmeter reading decreases [B1]",
    ],
    examinerExplanation: "Potential divider formula: V_out = V_supply × (R_th / (R1 + R_th)) = 6.0 × (2.4 / 3.6) = 4.0 V. As temperature rises, thermistor resistance drops, reducing V_out.",
    formulaUsed: "V_out = V_in · (R2 / (R1 + R2))",
  },

  // =========================================================================
  // UNIT 5: Nuclear & Atomic Physics
  // =========================================================================
  {
    id: "q-u5-2025-fm-36",
    topicId: "unit-5",
    subTopicId: "5.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q36",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "An element has two isotopes. Which row compares the nucleus of one atom of each isotope?",
    options: [
      "A. the charge on each nucleus: different | the mass of each nucleus: different",
      "B. the charge on each nucleus: different | the mass of each nucleus: the same",
      "C. the charge on each nucleus: the same | the mass of each nucleus: different",
      "D. the charge on each nucleus: the same | the mass of each nucleus: the same",
    ],
    correctAnswer: "C",
    marks: 1,
    markScheme: [
      "Isotopes have identical proton numbers (same nuclear charge).",
      "Isotopes have different neutron numbers (different nuclear mass).",
      "Option C is correct.",
    ],
    examinerExplanation: "Isotopes are atoms of the same chemical element that have the same number of protons (same charge +Ze) but different numbers of neutrons, resulting in different mass/nucleon numbers.",
  },
  {
    id: "q-u5-2025-fm-38",
    topicId: "unit-5",
    subTopicId: "5.2",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q38",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "Which statement about alpha, beta and gamma emissions is correct?",
    options: [
      "A. Alpha-particles are lighter than beta-particles.",
      "B. Beta-particles are not deflected by magnetic fields.",
      "C. Gamma rays are more penetrating than alpha-particles and beta-particles.",
      "D. Gamma rays are more ionising than alpha-particles and beta-particles.",
    ],
    correctAnswer: "C",
    marks: 1,
    markScheme: [
      "Gamma rays have the greatest penetrating power (only stopped by thick lead/concrete).",
      "Alpha particles have the lowest penetrating power and highest ionising power.",
      "Option C is correct.",
    ],
    examinerExplanation: "Penetrating power order: Gamma > Beta > Alpha. Ionising power order: Alpha > Beta > Gamma. Alpha is a heavy Helium nucleus, Beta is an electron, and Gamma is uncharged EM radiation.",
  },
  {
    id: "q-u5-2025-p32-q11",
    topicId: "unit-5",
    subTopicId: "5.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q11",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) Complete the atomic particle table: Proton (relative charge +1, location: in the nucleus), Neutron (charge 0, location: in the nucleus), Electron (charge -1, location: orbiting the nucleus).\n(b) State the relative charge on an alpha particle.\n(c) Compare the penetrating abilities of alpha particles, beta particles, and gamma rays.",
    correctAnswer: "(a) Neutron charge = 0, Neutron location = in the nucleus, Electron charge = -1. (b) +2. (c) Alpha stopped by paper / a few cm of air; Beta stopped by a few mm of aluminium; Gamma only attenuated by thick lead or concrete.",
    marks: 6,
    markScheme: [
      "(a) Neutron charge 0 [1 mark]; Neutron location: nucleus [1 mark]; Electron charge -1 [1 mark]",
      "(b) +2 (or 2+) [1 mark]",
      "(c) Alpha is least penetrating / stopped by skin/paper [1 mark]; Beta penetrating through paper but stopped by aluminium; Gamma most penetrating / requires thick lead [1 mark]",
    ],
    examinerExplanation: "Alpha has charge +2 and is easily stopped by a sheet of paper. Beta (electron, charge -1) requires a few mm of aluminium. Gamma (neutral photon) penetrates deeply and requires thick lead.",
  },
  {
    id: "q-u5-cam-top-2018-halflife",
    topicId: "unit-5",
    subTopicId: "5.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/18 Q11",
    yearSession: "May/June 2018",
    difficulty: "Extended",
    questionText: "Radon-222 is represented as ²²²₈₆Rn.\n(a) For a neutral atom of radon-222, state the number of protons, neutrons, and electrons.\n(b) Radon-222 decays by alpha emission to polonium (Po). Complete the nuclide decay equation:\n²²²₈₆Rn → ...Po + ...α\n(c) Radon-222 has a half-life of 3.8 days. At t = 0, a sample contains 6.4 × 10⁶ radon nuclei. Calculate the number of alpha particles emitted in the following 7.6 days.",
    correctAnswer: "(a) 86 protons, 136 neutrons, 86 electrons. (b) ²¹⁸₈₄Po + ⁴₂α. (c) 4.8 × 10⁶ alpha particles.",
    marks: 7,
    markScheme: [
      "(a) Protons = 86, Electrons = 86, Neutrons = 222 - 86 = 136 [2 marks]",
      "(b) ²¹⁸₈₄Po + ⁴₂α (or ⁴₂He) [2 marks: mass 218 (1), proton 84 (1)]",
      "(c) Time 7.6 days = 7.6 / 3.8 = 2 half-lives [C1]\nNuclei remaining = 6.4 × 10⁶ / 2² = 1.6 × 10⁶ [C1]\nNumber of decays / alpha emitted = Initial - Remaining = 6.4 × 10⁶ - 1.6 × 10⁶ = 4.8 × 10⁶ [A1]",
    ],
    examinerExplanation: "Alpha emission reduces mass number by 4 (222 - 4 = 218) and atomic number by 2 (86 - 2 = 84). In 7.6 days (2 half-lives), ¾ of the nuclei decay: ¾ × 6.4 × 10⁶ = 4.8 × 10⁶ alpha particles emitted.",
    formulaUsed: "N(t) = N0 / 2^(t/t_half), Decayed = N0 - N(t)",
  },
  {
    id: "q-u5-cam-top-2019-rutherford",
    topicId: "unit-5",
    subTopicId: "5.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/41/M/J/19 Q9",
    yearSession: "May/June 2019",
    difficulty: "Extended",
    questionText: "In the alpha particle scattering experiment, a narrow beam of alpha particles is directed at a thin sheet of gold in a vacuum.\nState what can be deduced about the structure of an atom from each of the following observations:\n(i) The majority of alpha particles pass straight through undeflected.\n(ii) A small number of alpha particles are deflected by small angles.\n(iii) A very small number of alpha particles are deflected by more than 90° (backscattered).",
    correctAnswer: "(i) Atom is mostly empty space. (ii) Nucleus has a concentrated positive charge. (iii) Nucleus is very small, dense, and contains almost all the mass of the atom.",
    marks: 6,
    markScheme: [
      "(i) Deduction: Most of the atom is empty space [B1]; because alpha particles pass through without encountering mass/charge [B1]",
      "(ii) Deduction: The nucleus has a positive charge [B1]; which exerts an electrostatic repulsive force on positive alpha particles [B1]",
      "(iii) Deduction: The nucleus is extremely small and contains almost all the mass of the atom [B1]; providing sufficient inertia to bounce alpha particles back [B1]",
    ],
    examinerExplanation: "Rutherford's conclusions: (1) Most undeflected -> empty space. (2) Small deflections -> positive nucleus repels positive alpha particles. (3) Large backscatters -> tiny, highly dense nucleus concentrated with mass.",
  },

  // =========================================================================
  // UNIT 6: Space Physics
  // =========================================================================
  {
    id: "q-u6-2025-fm-40",
    topicId: "unit-6",
    subTopicId: "6.1",
    paperType: "Paper 2 (MCQ)",
    paperCode: "0625/12/F/M/25 Q40",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "The diagram shows the Earth in orbit around the Sun. In position 1, the North Pole tilts directly away from the Sun.\nWhich row identifies and explains the correct season in the Northern Hemisphere at position 1?",
    options: [
      "A. summer | the northern hemisphere tilts away from the Sun",
      "B. summer | the northern hemisphere tilts towards the Sun",
      "C. winter | the northern hemisphere tilts away from the Sun",
      "D. winter | the northern hemisphere tilts towards the Sun",
    ],
    correctAnswer: "C",
    marks: 1,
    markScheme: [
      "When the Northern Hemisphere tilts away from the Sun, solar rays hit at a shallower angle and daylight hours are shorter.",
      "Season is Winter in the Northern Hemisphere.",
      "Option C is correct.",
    ],
    examinerExplanation: "The tilt of the Earth's rotational axis (approx 23.5°) causes the seasons. Tilting away from the Sun results in winter due to lower solar intensity and shorter days.",
  },
  {
    id: "q-u6-2025-p32-q12",
    topicId: "unit-6",
    subTopicId: "6.1",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/32/F/M/25 Q12",
    yearSession: "Feb/March 2025",
    difficulty: "Core",
    questionText: "(a) State the term used to describe the force that keeps the Earth in circular orbit around the Sun.\n(b) Explain why the Earth experiences an annual cycle of seasons.\n(c) The average distance of the Earth from the Sun is 1.48 × 10¹¹ m. The speed of light is 3.0 × 10⁸ m/s. Calculate the time it takes light from the Sun to reach the Earth in seconds.",
    correctAnswer: "(a) Gravitational force (centripetal force due to gravity), (b) The Earth's axis is tilted as it orbits the Sun, (c) 493 s (approx 8.2 minutes)",
    marks: 5,
    markScheme: [
      "(a) Gravitational force / gravity [1 mark]",
      "(b) The Earth's axis of rotation is tilted relative to its orbital plane [1 mark]",
      "(c) Formula: time = distance / speed = (1.48 × 10¹¹) / (3.0 × 10⁸) [C1, C1] = 493 s (or 490 s / 8.2 minutes) [A1]",
    ],
    examinerExplanation: "Gravitational attraction provides centripetal force for orbital motion. The 23.5° axial tilt causes varying sunlight angles throughout the year. Time t = d / c = 1.48×10¹¹ / 3.0×10⁸ = 493.3 s.",
    formulaUsed: "t = d / c",
  },
  {
    id: "q-u6-cam-top-2023-hubble",
    topicId: "unit-6",
    subTopicId: "6.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/M/J/23 Q10",
    yearSession: "May/June 2023",
    difficulty: "Extended",
    questionText: "(a) State the equation that defines the average orbital speed v of a planet in terms of orbital radius r and orbital period T.\n(b) A distant galaxy is moving away from Earth with a speed of 33 000 km/s. The value of the Hubble constant is H₀ = 2.2 × 10⁻¹⁸ s⁻¹. 1 light-year = 9.5 × 10¹⁵ m.\nCalculate the distance from the galaxy to Earth in light-years.",
    correctAnswer: "(a) v = 2πr / T, (b) 1.6 × 10⁹ light-years",
    marks: 5,
    markScheme: [
      "(a) v = 2πr / T (where r is orbital radius, T is orbital period) [B1, B1]",
      "(b) Hubble's law: v = H₀ · d => d = v / H₀ [C1]\nv = 33 000 km/s = 3.3 × 10⁷ m/s\nd = (3.3 × 10⁷) / (2.2 × 10⁻¹⁸) = 1.5 × 10²⁵ m [C1]\nd_ly = (1.5 × 10²⁵) / (9.5 × 10¹⁵) = 1.58 × 10⁹ light-years ≈ 1.6 × 10⁹ light-years [A1]",
    ],
    examinerExplanation: "Hubble's Law: v = H₀d => d = v / H₀ = (3.3 × 10⁷ m/s) / (2.2 × 10⁻¹⁸ s⁻¹) = 1.5 × 10²⁵ m. Convert meters to light-years: 1.5×10²⁵ / 9.5×10¹⁵ = 1.58 × 10⁹ ly.",
    formulaUsed: "v = 2πr / T, v = H₀ · d",
  },
  {
    id: "q-u6-cam-top-2023-stars",
    topicId: "unit-6",
    subTopicId: "6.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/42/O/N/23 Q10",
    yearSession: "Oct/Nov 2023",
    difficulty: "Extended",
    questionText: "Complete the statements describing the life cycle of stars:\n(a) Protostars are formed from clouds of interstellar .................... and dust called a nebula.\n(b) A protostar becomes a stable main sequence star when the inward gravitational force is balanced by outward .................... pressure due to high temperatures.\n(c) The initial nuclear fuel used in the core of all main sequence stars is .....................\n(d) Stars that are much more massive than the Sun explode as a ...................., leaving behind a neutron star or a .....................",
    correctAnswer: "(a) gas (nebula), (b) radiation / thermal, (c) hydrogen (fusing into helium), (d) supernova, black hole",
    marks: 5,
    markScheme: [
      "(a) gas / interstellar clouds / nebula [B1]",
      "(b) thermal pressure / radiation pressure [B1]",
      "(c) hydrogen [B1]",
      "(d) supernova [B1], black hole [B1]",
    ],
    examinerExplanation: "Stars form from gravitational collapse of interstellar gas (nebula). In main sequence equilibrium, gravity balances outward radiation pressure from hydrogen fusion. Massive stars expand into red supergiants, explode in a supernova, and leave behind a neutron star or black hole.",
  },
  {
    id: "q-u6-cam-top-2023-universe",
    topicId: "unit-6",
    subTopicId: "6.2",
    paperType: "Paper 4 (Extended)",
    paperCode: "0625/43/O/N/23 Q10",
    yearSession: "Oct/Nov 2023",
    difficulty: "Extended",
    questionText: "(a) What is represented in astrophysics by the symbol H₀?\n(b) Write down the equation that defines H₀ in terms of recessional speed v and distance d of a far galaxy.\n(c) Using the value H₀ = 2.2 × 10⁻¹⁸ s⁻¹, determine an estimate for the age of the Universe in seconds and in years (1 year ≈ 3.15 × 10⁷ s).\n(d) State the cosmic radiation evidence that supports the Big Bang Theory.",
    correctAnswer: "(a) Hubble constant. (b) H₀ = v / d. (c) 4.5 × 10¹⁷ s ≈ 1.4 × 10¹⁰ years (14 billion years). (d) Cosmic Microwave Background Radiation (CMBR).",
    marks: 6,
    markScheme: [
      "(a) Hubble constant [B1]",
      "(b) H₀ = v / d (or v = H₀ · d) [B1]",
      "(c) Age of Universe t = 1 / H₀ = 1 / (2.2 × 10⁻¹⁸) = 4.545 × 10¹⁷ s [C1]\nIn years: (4.545 × 10¹⁷) / (3.15 × 10⁷) = 1.44 × 10¹⁰ years (approx 14 billion years) [A1]",
      "(d) Cosmic Microwave Background Radiation (CMBR) detected from all directions in space [B2]",
    ],
    examinerExplanation: "The reciprocal of the Hubble constant gives an estimate of cosmic expansion time (the age of the Universe): t = 1/H₀ = 1 / (2.2 × 10⁻¹⁸) = 4.55 × 10¹⁷ s ≈ 14.4 billion years.",
    formulaUsed: "t_age = 1 / H₀, v = H₀ · d",
  },
];

// =========================================================================
// REAL CAMBRIDGE IGCSE EXAM PAPER PACKAGES
// =========================================================================
export const PAST_PAPER_EXAMS: PastPaperExam[] = [
  {
    id: "cambridge-2025-fm-p12",
    paperCode: "0625/12/F/M/25",
    title: "Cambridge IGCSE Physics 0625 - Paper 1 Multiple Choice (Feb/March 2025)",
    session: "Feb/March 2025",
    year: 2025,
    paperType: "Paper 2 (MCQ)",
    durationMinutes: 45,
    totalMarks: 40,
    gradeThresholds: {
      grade9: 35,
      grade8: 31,
      grade7: 27,
      grade6: 23,
      grade5: 19,
      grade4: 15,
    },
    questionIds: [
      "q-u1-2025-fm-1",
      "q-u1-2025-fm-2",
      "q-u1-2025-fm-3",
      "q-u1-2025-fm-4",
      "q-u1-2025-fm-5",
      "q-u1-2025-fm-6",
      "q-u1-2025-fm-8",
      "q-u1-2025-fm-9",
      "q-u1-2025-fm-11",
      "q-u1-2025-fm-12",
      "q-u1-2025-fm-13",
      "q-u2-2025-fm-15",
      "q-u2-2025-fm-17",
      "q-u2-2025-fm-20",
      "q-u3-2025-fm-21",
      "q-u3-2025-fm-24",
      "q-u3-2025-fm-27",
      "q-u4-2025-fm-29",
      "q-u4-2025-fm-32",
      "q-u4-2025-fm-34",
      "q-u5-2025-fm-36",
      "q-u5-2025-fm-38",
      "q-u6-2025-fm-40",
    ],
    instructions: [
      "There are forty multiple-choice questions on this paper.",
      "For each question there are four possible answers A, B, C, and D.",
      "Take the acceleration of free fall g to be 9.8 m/s².",
      "Each correct answer scores 1 mark. No marks deducted for incorrect answers.",
    ],
  },
  {
    id: "cambridge-2025-fm-p32",
    paperCode: "0625/32/F/M/25",
    title: "Cambridge IGCSE Physics 0625 - Paper 3 Theory (Feb/March 2025)",
    session: "Feb/March 2025",
    year: 2025,
    paperType: "Paper 4 (Extended)",
    durationMinutes: 75,
    totalMarks: 80,
    gradeThresholds: {
      grade9: 66,
      grade8: 58,
      grade7: 50,
      grade6: 42,
      grade5: 34,
      grade4: 26,
    },
    questionIds: [
      "q-u1-2025-p32-q1",
      "q-u1-2025-p32-q2",
      "q-u1-2025-p32-q3",
      "q-u2-2025-p32-q6",
      "q-u3-2025-p32-q7",
      "q-u4-2025-p32-q9",
      "q-u4-2025-p32-q10",
      "q-u5-2025-p32-q11",
      "q-u6-2025-p32-q12",
    ],
    instructions: [
      "Answer all questions in the spaces provided.",
      "Show all working clearly for calculation questions to gain full method marks.",
      "Give your answers to an appropriate number of significant figures (2 or 3 s.f.).",
      "Take the acceleration of free fall g = 9.8 m/s².",
    ],
  },
  {
    id: "cambridge-topical-paper4",
    paperCode: "0625/41-43/2017-2023",
    title: "Cambridge IGCSE Physics 0625 - Paper 4 Topical Master Collection",
    session: "2017 - 2023",
    year: 2023,
    paperType: "Paper 4 (Extended)",
    durationMinutes: 75,
    totalMarks: 80,
    gradeThresholds: {
      grade9: 68,
      grade8: 60,
      grade7: 52,
      grade6: 44,
      grade5: 36,
      grade4: 28,
    },
    questionIds: [
      "q-u1-cam-top-2017-q3",
      "q-u1-cam-top-2017-q1",
      "q-u1-cam-top-2017-impulse",
      "q-u1-cam-top-2017-collision",
      "q-u1-cam-top-2018-hooke",
      "q-u1-cam-top-2022-pulley",
      "q-u2-cam-top-2017-q4",
      "q-u2-cam-top-2018-shc",
      "q-u2-cam-top-2018-latent",
      "q-u3-cam-top-2017-snell",
      "q-u3-cam-top-2021-optic-fibre",
      "q-u4-cam-top-2018-potdiv",
      "q-u5-cam-top-2018-halflife",
      "q-u5-cam-top-2019-rutherford",
      "q-u6-cam-top-2023-hubble",
      "q-u6-cam-top-2023-stars",
      "q-u6-cam-top-2023-universe",
    ],
    instructions: [
      "Structured calculation and explanatory questions curated from Cambridge 0625 Paper 4 series.",
      "Each question includes official Cambridge mark scheme breakdown and examiner tips.",
    ],
  },
];
